import React, {useEffect, useImperativeHandle, useState} from "react";
import {ExpandLess, ExpandMore} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Card,
  CardHeader,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Call from "../../hocs/call";
import GroupedSelect from "../grouped-select";
import HierarchicalSelect from "../hierarchical-select";
import {nodeSelector} from "../../state/node/nodeSelectors";
import {
  getEasyLoadingOrganizations,
  getEasyLoadingPackageLicense,
  getEasyLoadingResourceLicense,
  getEasyLoadingThemes,
  PACKAGE_MANAGER_EASY_LOADING_THEMES_SPINNER
} from "../../state/package-manager/packageManagerActions";
import {easyLoadingSelector} from "../../state/package-manager/packageManagerSelectors";
import {addSpinnerMessage, markSpinnerMessage} from "../../state/spinner/spinnerActions";

export const MAX_TITLE_LENGTH = 100;

const EasyLoadingForm = ({ref, nodeId, dataset, setDataset, resource, setResource, sourceField, disabled}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const node = useSelector(nodeSelector);
  const {themes, organizations, packageLicenses, resourceLicenses} = useSelector(easyLoadingSelector);

  const isDataportalNode = node.type === "Dataportal";

  const fetchThemes = ({nodeId}) => {
    dispatch(
      addSpinnerMessage(
        PACKAGE_MANAGER_EASY_LOADING_THEMES_SPINNER,
        t("modules.packageManager.actions.messages.getEtlThemes")
      )
    );
    dispatch(getEasyLoadingThemes(nodeId, node.type));
    dispatch(getEasyLoadingOrganizations(nodeId, node.type));
    if (isDataportalNode) {
      dispatch(getEasyLoadingPackageLicense(nodeId));
      dispatch(getEasyLoadingResourceLicense(nodeId));
    }
  };

  useEffect(() => {
    if (themes && organizations && (!isDataportalNode || (packageLicenses && resourceLicenses))) {
      dispatch(markSpinnerMessage(PACKAGE_MANAGER_EASY_LOADING_THEMES_SPINNER));
    }
  }, [themes, organizations, packageLicenses, resourceLicenses, isDataportalNode, dispatch]);

  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(prev => !prev);
  useImperativeHandle(ref, () => ({
    setExpanded: expanded => setExpanded(expanded)
  }));

  return (
    <Call cb={fetchThemes} cbParam={{nodeId: nodeId}} disabled={!nodeId || Boolean(themes && organizations)}>
      <Box className="package-manager__easy-loading-form">
        <Grid container spacing={2} className="package-manager__easy-loading-form__visible-fields">
          <Grid className="package-manager__easy-loading-form__source-item" size={12}>
            {sourceField ? sourceField : null}
          </Grid>
          <Grid className="package-manager__easy-loading-form__title-item" size={12}>
            <TextField
              className="package-manager__easy-loading-form__title-field"
              label={t("modules.packageManager.easyLoadingForm.fields.title.label")}
              variant="outlined"
              value={dataset?.title ?? ""}
              onChange={e => {
                const value = e?.target?.value ? e.target.value.slice(0, MAX_TITLE_LENGTH) : null;
                setDataset({...dataset, title: value});
                setResource({...resource, title: value});
              }}
              slotProps={{
                input: {
                  inputProps: {
                    maxLength: MAX_TITLE_LENGTH
                  }
                }
              }}
              disabled={disabled}
              error={dataset?.hasError && !dataset.title}
              helperText={
                dataset?.hasError &&
                !dataset.title &&
                t("modules.packageManager.easyLoadingForm.errors.invalidDatasetTitle")
              }
              required
              fullWidth
            />
          </Grid>
          <Grid className="package-manager__easy-loading-form__theme-item" size={12}>
            <HierarchicalSelect
              items={themes || []}
              value={dataset?.theme}
              onChange={val => {
                setDataset({...dataset, theme: val});
              }}
              label={t("modules.packageManager.easyLoadingForm.fields.theme.label")}
              className="package-manager__easy-loading-form__theme-field"
              disabled={disabled}
              error={dataset?.hasError && !dataset.theme}
              helperText={
                dataset?.hasError && !dataset.theme && t("modules.packageManager.easyLoadingForm.errors.missingTheme")
              }
              required
              clearable
            />
          </Grid>
        </Grid>

        <Card
          variant="outlined"
          className="package-manager__easy-loading-form__collapsable-fields"
          sx={{marginTop: "16px"}}
        >
          <CardHeader
            onClick={toggleExpanded}
            title={<Typography>{t("modules.packageManager.easyLoadingForm.fields.moreInfo.label")}</Typography>}
            action={<IconButton>{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>}
            sx={{cursor: "pointer"}}
          />
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Grid container spacing={2} sx={{padding: "0 16px 16px", marginTop: "8px"}}>
              <Grid className="package-manager__easy-loading-form__description-item" size={12}>
                <TextField
                  className="package-manager__easy-loading-form__description-field"
                  label={t("modules.packageManager.easyLoadingForm.fields.description.label")}
                  variant="outlined"
                  value={dataset?.description ?? ""}
                  onChange={e => {
                    setDataset({...dataset, description: e.target.value});
                  }}
                  disabled={disabled}
                  error={dataset?.hasError && !dataset.description}
                  helperText={
                    dataset?.hasError &&
                    !dataset.description &&
                    t("modules.packageManager.easyLoadingForm.errors.invalidDatasetDescription")
                  }
                  required
                  fullWidth
                  multiline
                  minRows={5}
                  maxRows={20}
                />
              </Grid>
              <Grid className="package-manager__easy-loading-form__organization-item" size={12}>
                <TextField
                  select
                  className="package-manager__easy-loading-form__organization-field"
                  label={t("modules.packageManager.easyLoadingForm.fields.source.label")}
                  variant="outlined"
                  value={dataset?.organization ?? ""}
                  onChange={e => {
                    setDataset({...dataset, organization: e.target.value});
                  }}
                  disabled={disabled}
                  error={dataset?.hasError && !dataset.organization}
                  helperText={
                    dataset?.hasError &&
                    !dataset.organization &&
                    t("modules.packageManager.easyLoadingForm.errors.invalidDatasetOrganization")
                  }
                  fullWidth
                  required
                >
                  {(organizations || []).map(({id, label}) => (
                    <MenuItem key={id} value={id}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {isDataportalNode && (
                <>
                  <Grid className="package-manager__easy-loading-form__package-license-item" size={12}>
                    <GroupedSelect
                      className="package-manager__easy-loading-form__package-license-field"
                      label={t("modules.packageManager.easyLoadingForm.fields.packageLicense.label")}
                      items={packageLicenses || []}
                      value={dataset?.license}
                      onChange={val => {
                        setDataset({...dataset, license: val});
                      }}
                      disabled={disabled}
                      error={dataset?.hasError && !dataset.license}
                      helperText={
                        dataset?.hasError &&
                        !dataset.license &&
                        t("modules.packageManager.easyLoadingForm.errors.invalidDatasetLicense")
                      }
                      required
                    />
                  </Grid>
                  <Grid className="package-manager__easy-loading-form__resource-license-item" size={12}>
                    <GroupedSelect
                      className="package-manager__easy-loading-form__resource-license-field"
                      label={t("modules.packageManager.easyLoadingForm.fields.resourceLicense.label")}
                      items={resourceLicenses || []}
                      value={resource?.license}
                      onChange={val => {
                        setResource({...resource, license: val});
                      }}
                      disabled={disabled}
                      error={resource?.hasError && !resource.license}
                      helperText={
                        resource?.hasError &&
                        !resource.license &&
                        t("modules.packageManager.easyLoadingForm.errors.invalidResourceLicense")
                      }
                      required
                    />
                  </Grid>
                </>
              )}
              <Grid className="package-manager__easy-loading-form__keywords-item" size={12}>
                <Autocomplete
                  className="package-manager__easy-loading-form__keywords-field"
                  multiple
                  freeSolo
                  options={[]}
                  value={dataset?.keywords ?? []}
                  onChange={(_e, value) => {
                    setDataset({...dataset, keywords: value});
                  }}
                  disabled={disabled}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t("modules.packageManager.easyLoadingForm.fields.keywords.label")}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Box>
    </Call>
  );
};

export default EasyLoadingForm;
